import { ContactsView } from "@/components/contact/ContactsView";
import AntIcon from "@/components/icons/AntIcon";
import PopconfirmDeleteIcon from "@/components/icons/PopconfirmDeleteIcon";
import CopyToClipboardView from "@/components/views/CopyToClipboardView";
import HtmlView from "@/components/views/HtmlView";
import { formatAddress, formatIban, formatLocaleDate } from "@/utils/formatUtils";
import { tBoolean } from "@/utils/translationUtils";
import { Button, Descriptions, Divider, Popconfirm, Tag } from "antd";
import { DescriptionsItemType } from "antd/es/descriptions";
import dayjs from "dayjs";
import { Fragment, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { generatePath, Link, useNavigate } from "react-router";
import { useDeleteClient, useGetClientPartnerAgent } from "../../../../common/api/queries";
import { Permission } from "../../../../common/security/enums";
import { formatAgentName } from "../../../agent/utils";
import ComponentWithPermission from "../../../auth/components/ComponentWithPermission";
import { useHasAnyPermission, useHasPermissions } from "../../../auth/hooks";
import { CLIENT_ROUTE_PATHS } from "../../paths";
import { Client } from "../../types";
import { isLegalClient, isNaturalClient, isSelfEmployedClient, resolveClientAddressLabel } from "../../utils";
import { ClientContractsTable } from "../tables/ClientContractsTable";

const DESCRIPTION_LABEL_WIDTH = 210;

interface Props {
  client: Client;
  onUpdateClick?: () => void;
  showDelete?: boolean;
  isNarrowView?: boolean;
}

export const ClientDetailView = ({ client, onUpdateClick, showDelete, isNarrowView }: Props) => {
  const { t } = useTranslation();

  const navigate = useNavigate();
  const deleteClient = useDeleteClient();

  // The drawer also opens from contract details, where the viewer may lack CLIENT_READ that the endpoint requires
  const hasClientReadPermission = useHasPermissions(Permission.CLIENT_READ);
  const { data: clientPartnerAgent } = useGetClientPartnerAgent(client.id, hasClientReadPermission);

  const hasReadContractPermission = useHasAnyPermission(
    Permission.INSURANCE_READ,
    Permission.LOAN_READ,
    Permission.INVESTMENT_READ,
    Permission.DEPOSIT_READ,
    Permission.SECOND_PILLAR_READ,
    Permission.THIRD_PILLAR_READ,
    Permission.GENERIC_READ,
  );

  const items = useMemo(() => {
    const items: DescriptionsItemType[] = [];

    if (!isNaturalClient(client)) {
      items.push({
        label: t("agent.attrs.companyName"),
        children: client.companyName,
      });
    } else {
      items.push({
        label: t("agent.attrs.aggregatedName"),
        children: client.aggregatedName,
      });
    }

    if (isNaturalClient(client)) {
      items.push({
        label: t("client.titles.basicData"),
        children: (
          <Descriptions
            size="small"
            column={isNarrowView ? 1 : 2}
            bordered
            styles={{ label: { width: DESCRIPTION_LABEL_WIDTH } }}
            items={[
              {
                label: t("client.attrs.managedBy"),
                children: formatAgentName(client.managedBy),
                span: isNarrowView ? 1 : 2,
              },
              {
                label: t("client.attrs.partnerIdNumber"),
                children: clientPartnerAgent?.partnerIdNumber,
                span: isNarrowView ? 1 : 2,
              },
              {
                label: t("client.attrs.bankAccountNumber"),
                children: (
                  <CopyToClipboardView
                    content={client.bankAccountNumber ? formatIban(client.bankAccountNumber) : undefined}
                  />
                ),
              },
              {
                label: t("client.attrs.bankSwift"),
                children: client.bankSwift,
              },
            ]}
          />
        ),
      });

      items.push({
        label: t("client.titles.personalData"),
        children: (
          <Descriptions
            size="small"
            column={isNarrowView ? 1 : 2}
            bordered
            styles={{ label: { width: DESCRIPTION_LABEL_WIDTH } }}
            items={[
              {
                label: t("client.attrs.personalIdentificationNumberLong"),
                children: <CopyToClipboardView content={client.personalIdentificationNumber} />,
              },
              {
                label: t("client.attrs.birthDate"),
                children: `${formatLocaleDate(client.birthDate)} (${t("client.helpers.age")}: ${dayjs().diff(
                  dayjs(client.birthDate),
                  "years",
                )})`,
              },
              {
                label: t("client.attrs.nationalityLabel"),
                children: client.nationality,
              },
              {
                label: t("client.attrs.birthPlace"),
                children: client.birthPlace,
              },
              {
                label: t("client.enums.clientIdentityCardType._label"),
                children: t(`client.enums.clientIdentityCardType.${client.identityCardType}`),
              },
              {
                label: t("client.attrs.identityCardNumber"),
                children: <CopyToClipboardView content={client.identityCardNumber} />,
              },
              {
                label: t("client.attrs.drivingLicenseYear"),
                children: client.drivingLicenseYear,
              },
            ]}
          />
        ),
      });

      items.push({
        label: t("client.titles.additionalData"),
        children: (
          <Descriptions
            column={isNarrowView ? 1 : 3}
            size="small"
            bordered
            styles={{ label: { width: DESCRIPTION_LABEL_WIDTH } }}
            items={[
              {
                label: t("client.attrs.verifiedByOcr"),
                children: tBoolean(client.verifiedByOcr),
              },
              {
                label: t("client.attrs.verifiedByBiometry"),
                children: tBoolean(client.verifiedByBiometry),
              },
              {
                label: t("client.attrs.sanctioned"),
                children: tBoolean(client.sanctioned),
              },
              {
                label: t("client.attrs.involvedInCrime"),
                children: tBoolean(client.involvedInCrime),
              },
              {
                label: t("client.attrs.pep"),
                children: tBoolean(client.pep),
                span: isNarrowView ? 1 : 2,
              },
              {
                label: t("client.attrs.note"),
                children: client.note && <HtmlView value={client.note} />,
                span: isNarrowView ? 1 : 3,
              },
            ]}
          />
        ),
      });
    } else if (isSelfEmployedClient(client)) {
      items.push({
        label: t("client.titles.basicData"),
        children: (
          <Descriptions
            size="small"
            column={isNarrowView ? 1 : 2}
            bordered
            styles={{ label: { width: DESCRIPTION_LABEL_WIDTH } }}
            items={[
              {
                label: t("client.attrs.aggregatedNaturalName"),
                children: client.aggregatedNaturalName,
                span: isNarrowView ? 1 : 2,
              },
              {
                label: t("client.attrs.managedBy"),
                children: formatAgentName(client.managedBy),
                span: isNarrowView ? 1 : 2,
              },
              {
                label: t("client.attrs.partnerIdNumber"),
                children: clientPartnerAgent?.partnerIdNumber,
                span: isNarrowView ? 1 : 2,
              },
              {
                label: t("client.attrs.bankAccountNumber"),
                children: (
                  <CopyToClipboardView
                    content={client.bankAccountNumber ? formatIban(client.bankAccountNumber) : undefined}
                  />
                ),
              },
              {
                label: t("client.attrs.bankSwift"),
                children: client.bankSwift,
              },
            ]}
          />
        ),
      });

      items.push({
        label: t("client.titles.companyData"),
        children: (
          <Descriptions
            size="small"
            column={isNarrowView ? 1 : 2}
            styles={{ label: { width: DESCRIPTION_LABEL_WIDTH } }}
            bordered
            items={[
              {
                label: t("client.attrs.companyRegistrationNumber"),
                children: <CopyToClipboardView content={client.companyRegistrationNumber} />,
              },
              {
                label: t("client.attrs.districtOffice"),
                children: client.districtOffice,
              },
              {
                label: t("client.attrs.taxId"),
                children: <CopyToClipboardView content={client.taxId} />,
              },
              {
                label: t("client.attrs.tradeRegisterNumber"),
                children: client.tradeRegisterNumber,
              },
              {
                label: t("client.attrs.vatId"),
                children: <CopyToClipboardView content={client.vatId} />,
              },
            ]}
          />
        ),
      });

      items.push({
        label: t("client.titles.personalData"),
        children: (
          <Descriptions
            size="small"
            column={isNarrowView ? 1 : 2}
            bordered
            styles={{ label: { width: DESCRIPTION_LABEL_WIDTH } }}
            items={[
              {
                label: t("client.attrs.personalIdentificationNumberLong"),
                children: <CopyToClipboardView content={client.personalIdentificationNumber} />,
              },
              {
                label: t("client.attrs.birthDate"),
                children: `${formatLocaleDate(client.birthDate)} (${t("client.helpers.age")}: ${dayjs().diff(
                  dayjs(client.birthDate),
                  "years",
                )})`,
              },
              {
                label: t("client.attrs.nationalityLabel"),
                children: client.nationality,
              },
              {
                label: t("client.attrs.birthPlace"),
                children: client.birthPlace,
              },
              {
                label: t("client.enums.clientIdentityCardType._label"),
                children: t(`client.enums.clientIdentityCardType.${client.identityCardType}`),
              },
              {
                label: t("client.attrs.identityCardNumber"),
                children: <CopyToClipboardView content={client.identityCardNumber} />,
              },
              {
                label: t("client.attrs.drivingLicenseYear"),
                children: client.drivingLicenseYear,
              },
            ]}
          />
        ),
      });

      items.push({
        label: t("client.titles.additionalData"),
        children: (
          <Descriptions
            column={isNarrowView ? 1 : 2}
            size="small"
            bordered
            styles={{ label: { width: DESCRIPTION_LABEL_WIDTH } }}
            items={[
              {
                label: t("client.attrs.verifiedByOcr"),
                children: tBoolean(client.verifiedByOcr),
              },
              {
                label: t("client.attrs.verifiedByBiometry"),
                children: tBoolean(client.verifiedByBiometry),
              },
              {
                label: t("client.attrs.sanctioned"),
                children: tBoolean(client.sanctioned),
              },
              {
                label: t("client.attrs.involvedInCrime"),
                children: tBoolean(client.involvedInCrime),
              },
              {
                label: t("client.attrs.pep"),
                children: tBoolean(client.pep),
                span: isNarrowView ? 1 : 2,
              },
              {
                label: t("client.attrs.note"),
                children: client.note && <HtmlView value={client.note} />,
                span: isNarrowView ? 1 : 3,
              },
            ]}
          />
        ),
      });
    } else if (isLegalClient(client)) {
      items.push({
        label: t("client.titles.basicData"),
        children: (
          <Descriptions
            size="small"
            column={isNarrowView ? 1 : 2}
            bordered
            styles={{ label: { width: DESCRIPTION_LABEL_WIDTH } }}
            items={[
              {
                label: t("client.attrs.managedBy"),
                children: formatAgentName(client.managedBy),
                span: isNarrowView ? 1 : 2,
              },
              {
                label: t("client.attrs.partnerIdNumber"),
                children: clientPartnerAgent?.partnerIdNumber,
                span: isNarrowView ? 1 : 2,
              },
              {
                label: t("client.attrs.bankAccountNumber"),
                children: (
                  <CopyToClipboardView
                    content={client.bankAccountNumber ? formatIban(client.bankAccountNumber) : undefined}
                  />
                ),
              },
              {
                label: t("client.attrs.bankSwift"),
                children: client.bankSwift,
              },
            ]}
          />
        ),
      });

      items.push({
        label: t("client.titles.companyData"),
        children: (
          <Descriptions
            size="small"
            column={isNarrowView ? 1 : 2}
            bordered
            styles={{ label: { width: DESCRIPTION_LABEL_WIDTH } }}
            items={[
              {
                label: t("client.attrs.companyRegistrationNumber"),
                children: <CopyToClipboardView content={client.companyRegistrationNumber} />,
              },
              {
                label: t("client.attrs.commercialRegisterDistrictOffice"),
                children: client.commercialRegisterDistrictOffice,
              },
              {
                label: t("client.attrs.taxId"),
                children: <CopyToClipboardView content={client.taxId} />,
              },
              {
                label: t("client.attrs.commercialRegisterSection"),
                children: client.commercialRegisterSection,
              },
              {
                label: t("client.attrs.vatId"),
                children: <CopyToClipboardView content={client.vatId} />,
              },
              {
                label: t("client.attrs.commercialRegisterInsertionNumber"),
                children: client.commercialRegisterInsertionNumber,
              },
            ]}
          />
        ),
      });

      items.push({
        label: t("client.titles.representatives"),
        children: client.representatives?.map((representative, index) => (
          <Fragment key={representative.id}>
            {index > 0 && ", "}
            <span className="nowrap">
              <Link
                to={generatePath(CLIENT_ROUTE_PATHS.detail.to, { id: representative.representative.id })}
                target="_blank"
              >
                {representative.representative.aggregatedName}
              </Link>
              {" - "}
              <span>{t("client.enums.representativeFunction." + representative.function)}</span>
            </span>
          </Fragment>
        )),
      });

      items.push({
        label: t("client.titles.additionalData"),
        children: (
          <Descriptions
            column={1}
            size="small"
            bordered
            styles={{ label: { width: DESCRIPTION_LABEL_WIDTH } }}
            items={[
              {
                label: t("client.attrs.note"),
                children: client.note && <HtmlView value={client.note} />,
              },
            ]}
          />
        ),
      });
    }

    items.push({
      label: t("client.titles.addresses"),
      children: (
        <Descriptions
          size="small"
          bordered
          column={1}
          styles={{ label: { width: DESCRIPTION_LABEL_WIDTH } }}
          items={[
            {
              label: resolveClientAddressLabel(client.type, "nationality" in client ? client.nationality : undefined),
              children: formatAddress(client.address),
            },
            {
              label: t("client.attrs.correspondenceAddress"),
              children: client.correspondenceAddress ? formatAddress(client.correspondenceAddress) : undefined,
            },
          ]}
        />
      ),
    });

    items.push({
      label: t("client.titles.contacts"),
      children: <ContactsView contacts={client.contacts} marketingConsentsAreApplicable showMainContactsDivider />,
    });

    return items;
  }, [client, clientPartnerAgent]);

  return (
    <>
      <div className="margin-bottom-tiny">
        {(isLegalClient(client) || isSelfEmployedClient(client)) && client.vatId && (
          <Tag color="blue">{t("agent.helpers.vatPayer")}</Tag>
        )}
      </div>

      <Descriptions styles={{ label: { fontWeight: "bold" } }} column={1} bordered items={items} />

      {hasReadContractPermission && (
        <div className="margin-top-large">
          <Divider titlePlacement="start">{t("client.titles.contracts")}</Divider>
          <ClientContractsTable clientId={client.id} />
        </div>
      )}

      <div className="margin-top-large">
        {onUpdateClick && (
          <ComponentWithPermission permissions={[Permission.CLIENT_UPDATE]}>
            <Button className="margin-right-tiny" type="primary" onClick={onUpdateClick} icon={<AntIcon type="edit" />}>
              {t("common.edit")}
            </Button>
          </ComponentWithPermission>
        )}

        {showDelete && (
          <ComponentWithPermission permissions={[Permission.CLIENT_DELETE]}>
            <Popconfirm
              title={t("client.titles.deleteConfirm", { aggregatedName: client.aggregatedName })}
              icon={<PopconfirmDeleteIcon />}
              okText={t("common.yes")}
              cancelText={t("common.no")}
              okType="danger"
              onConfirm={() =>
                deleteClient.mutate(client.id, {
                  onSuccess: () => navigate(CLIENT_ROUTE_PATHS.list.to, { replace: true }),
                })
              }
            >
              <Button icon={<AntIcon type="delete" />} danger>
                {t("common.delete")}
              </Button>
            </Popconfirm>
          </ComponentWithPermission>
        )}
      </div>
    </>
  );
};
